import { useMantineColorScheme } from "@mantine/core";
import { useMemo } from "react";
import type { Layout, Config, Shape } from "plotly.js";

// ============================================================================
// Configuration Constants
// ============================================================================

const COLOR_ADAPTATION_CONFIG = {
    LUMINANCE_THRESHOLD: 0.7,
    DARK_MODE_FACTOR: 0.6,
    DARK_MODE_ALPHA: 0.5,
    BLACK_VARIANTS: ["black", "#000", "#000000"],
} as const;

const THEME_COLORS = {
    dark: {
        paper: "rgba(36, 36, 36, 0.95)",
        plot: "rgba(46, 46, 46, 0.95)",
        text: "#c9c9c9",
        textSecondary: "#b8b8b8",
        grid: "rgba(130, 130, 130, 0.3)",
        line: "#696969",
        legendBg: "rgba(46, 46, 46, 0.8)",
    },
    light: {
        paper: "rgba(255, 255, 255, 0.95)",
        plot: "rgba(245, 249, 255, 0.95)",
        text: "#212529",
        textSecondary: "#495057",
        title: "#00053d",
        grid: "rgba(0, 5, 61, 0.1)",
        line: "#999bb1",
        legendBg: "rgba(255, 255, 255, 0.9)",
        legendBorder: "#cccdd8",
    },
} as const;

const COLORWAYS = {
    dark: ["#70B1FF", "#70F1CA", "#B899FF", "#ff7f0e", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#bcbd22", "#17becf"],
    light: ["#3290FF", "#32EBB3", "#6B70FF", "#ff7f0e", "#d62728", "#9467bd", "#8c564b", "#e377c2", "#bcbd22", "#17becf"],
} as const;

// BIOS branded font - Beatrice for charts and visualizations
const FONT_FAMILY = '"Beatrice", Verdana, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

// ============================================================================
// Color Utilities
// ============================================================================

interface RGB {
    r: number;
    g: number;
    b: number;
    a?: number;
}

/**
 * Parse any color string (hex, rgb, rgba) into RGB components
 */
const parseColor = (color: string): RGB | null => {
    const lower = color.toLowerCase().trim();

    // Parse hex
    if (lower.startsWith("#")) {
        const hex = lower.replace("#", "");
        let r: number, g: number, b: number;

        if (hex.length === 3) {
            r = parseInt(hex[0] + hex[0], 16);
            g = parseInt(hex[1] + hex[1], 16);
            b = parseInt(hex[2] + hex[2], 16);
        } else if (hex.length === 6 || hex.length === 8) {
            r = parseInt(hex.substring(0, 2), 16);
            g = parseInt(hex.substring(2, 4), 16);
            b = parseInt(hex.substring(4, 6), 16);
        } else {
            return null;
        }

        return { r, g, b };
    }

    // Parse rgb/rgba
    const rgbMatch = lower.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (rgbMatch) {
        return {
            r: parseInt(rgbMatch[1]),
            g: parseInt(rgbMatch[2]),
            b: parseInt(rgbMatch[3]),
            a: rgbMatch[4] ? parseFloat(rgbMatch[4]) : 1,
        };
    }

    return null;
};

/**
 * Calculate relative luminance (0-1) using standard formula
 */
const calculateLuminance = (rgb: RGB): number => {
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
};

/**
 * Adapt color for dark mode if it's too light
 */
const adaptColorForDarkMode = (color: string | undefined): string | undefined => {
    if (!color) return color;

    const rgb = parseColor(color);
    if (!rgb) return color;

    const luminance = calculateLuminance(rgb);

    // Only adapt colors that are too light
    if (luminance <= COLOR_ADAPTATION_CONFIG.LUMINANCE_THRESHOLD) {
        return color;
    }

    // Darken and add transparency
    const { DARK_MODE_FACTOR, DARK_MODE_ALPHA } = COLOR_ADAPTATION_CONFIG;
    const newR = Math.round(rgb.r * DARK_MODE_FACTOR);
    const newG = Math.round(rgb.g * DARK_MODE_FACTOR);
    const newB = Math.round(rgb.b * DARK_MODE_FACTOR);
    const alpha = rgb.a ? Math.min(rgb.a, DARK_MODE_ALPHA) : DARK_MODE_ALPHA;

    return `rgba(${newR}, ${newG}, ${newB}, ${alpha})`;
};

/**
 * Check if color is a black variant
 */
const isBlackColor = (color: string | undefined): boolean => {
    if (!color) return false;
    const normalized = color.toLowerCase().trim();
    return COLOR_ADAPTATION_CONFIG.BLACK_VARIANTS.some(variant => variant === normalized);
};


// ============================================================================
// Dark Mode Adaptation Functions
// ============================================================================

/**
 * Adapt object properties that contain colors for dark mode
 */
const adaptColorProperty = <T extends Record<string, any>>(
    obj: T | undefined,
    colorProperties: (keyof T)[],
    isDark: boolean
): T | undefined => {
    if (!obj || !isDark) return obj;

    const adapted = { ...obj };
    for (const prop of colorProperties) {
        if (adapted[prop]) {
            const adaptedColor = adaptColorForDarkMode(adapted[prop] as string);
            if (adaptedColor !== undefined) {
                adapted[prop] = adaptedColor as T[keyof T];
            }
        }
    }
    return adapted;
};

/**
 * Adapt shapes array for dark mode
 */
export const adaptShapesForDarkMode = (
    shapes: Partial<Shape>[] | undefined,
    isDark: boolean
): Partial<Shape>[] | undefined => {
    if (!shapes || !isDark) return shapes;

    return shapes.map(shape => ({
        ...shape,
        fillcolor: adaptColorForDarkMode(shape.fillcolor as string),
        line: shape.line ? adaptColorProperty(shape.line, ["color"], isDark) : shape.line,
    }));
};

/**
 * Adapt annotations for dark mode
 */
export const adaptAnnotationsForDarkMode = (
    annotations: Partial<Plotly.Annotations>[] | undefined,
    isDark: boolean
): Partial<Plotly.Annotations>[] | undefined => {
    if (!annotations || !isDark) return annotations;

    return annotations.map(annotation => {
        const adapted: Partial<Plotly.Annotations> = { ...annotation };

        // Adapt background color
        adapted.bgcolor = adaptColorForDarkMode(annotation.bgcolor as string);

        // Adapt border color (convert black to softer gray)
        if (annotation.bordercolor) {
            adapted.bordercolor = isBlackColor(annotation.bordercolor as string)
                ? THEME_COLORS.dark.line
                : adaptColorForDarkMode(annotation.bordercolor as string);
        }

        // Ensure text is visible (convert black to light)
        const fontColor = annotation.font?.color as string | undefined;
        adapted.font = {
            ...annotation.font,
            color: isBlackColor(fontColor) ? THEME_COLORS.dark.text : (fontColor || THEME_COLORS.dark.text),
        };

        return adapted;
    });
};


// ============================================================================
// Theme Layout Builder
// ============================================================================

/**
 * Create axis configuration for a given theme
 */
const createAxisConfig = (colors: typeof THEME_COLORS.dark | typeof THEME_COLORS.light) => ({
    gridcolor: colors.grid,
    linecolor: colors.line,
    tickcolor: colors.line,
    tickfont: { color: colors.textSecondary },
    title: { font: { color: colors.text } },
    zerolinecolor: colors.line,
});

/**
 * Create theme layout for light or dark mode
 */
const createThemeLayout = (isDark: boolean): Partial<Layout> => {
    const colors = isDark ? THEME_COLORS.dark : THEME_COLORS.light;
    const colorway = isDark ? COLORWAYS.dark : COLORWAYS.light;
    const axisConfig = createAxisConfig(colors);

    return {
        paper_bgcolor: colors.paper,
        plot_bgcolor: colors.plot,
        font: {
            color: colors.text,
            family: FONT_FAMILY,
        },
        title: {
            font: { color: (colors as any).title || colors.text },
        },
        xaxis: axisConfig,
        yaxis: axisConfig,
        yaxis2: axisConfig,
        legend: {
            bgcolor: colors.legendBg,
            bordercolor: (colors as any).legendBorder || colors.line,
            font: { color: colors.text },
        },
        colorway: [...colorway],
    };
};

/**
 * Plotly theme hook for light/dark mode
 */
export const usePlotlyTheme = () => {
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === "dark";

    const themeLayout = useMemo(() => createThemeLayout(isDark), [isDark]);

    const themeConfig: Partial<Config> = useMemo(() => ({
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: ["lasso2d", "select2d"],
    }), []);

    return { themeLayout, themeConfig, isDark };
};

/**
 * Deep merge two objects, with source values taking precedence
 * Used to merge theme layout with backend-provided layout
 */
export const deepMergeLayout = <T extends object>(target: T, source: Partial<T>): T => {
    const result = { ...target } as T;

    for (const key in source) {
        if (source.hasOwnProperty(key)) {
            const sourceValue = source[key];
            const targetValue = (target as any)[key];

            if (
                sourceValue !== null &&
                typeof sourceValue === "object" &&
                !Array.isArray(sourceValue) &&
                targetValue !== null &&
                typeof targetValue === "object" &&
                !Array.isArray(targetValue)
            ) {
                (result as any)[key] = deepMergeLayout(targetValue, sourceValue);
            } else if (sourceValue !== undefined) {
                (result as any)[key] = sourceValue;
            }
        }
    }

    return result;
};

